import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Card, Chip, Container, Divider, Grid,
  Stack, Typography, useTheme, useMediaQuery,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
  Business as BuildingIcon,
  Shield as ShieldIcon,
  Visibility as EyeIcon,
  FactCheck as InspectIcon,
} from "@mui/icons-material";

// ─── Data ─────────────────────────────────────────────────────────────────────

const MODULES = [
  {
    slug:    "weldassure",
    label:   "WeldAssure",
    price:   "$25 / mo",
    priceNote: "or $244.99 / yr · after trial",
    tagline: "Heat Traceability & CMTR Management",
    color:   "#1565c0",
    bg:      "#e3f2fd",
    icon:    <ShieldIcon sx={{ fontSize: 36 }} />,
    features: [
      "Incoming heat number log with full material genealogy",
      "CMTR upload, review, and compliance verification",
      "Engineering release package control",
      "Quarantine management with full audit trail",
      "Heat-to-piece-mark traceability chain",
    ],
  },
  {
    slug:    "sfm_inspection_plus",
    label:   "SFM Inspection Plus",
    price:   "$25 / mo",
    priceNote: "or $244.99 / yr · after trial",
    tagline: "QA/NCR & Dimensional Inspection",
    color:   "#6a1b9a",
    bg:      "#f3e5f5",
    icon:    <InspectIcon sx={{ fontSize: 36 }} />,
    features: [
      "Non-conformance reports with auto-numbered NCR log",
      "Disposition tracking: use-as-is, rework, reject, scrap",
      "Root cause and corrective action management",
      "Cost of poor quality (COPQ) tracking",
      "Audit-ready NCR history with diff viewer",
    ],
  },
  {
    slug:    "sfm_live_operations",
    label:   "SFM Operations",
    price:   "Free",
    priceNote: "included with every account",
    tagline: "Live Job Tracking & Chain of Custody",
    color:   "#1b5e20",
    bg:      "#e8f5e9",
    icon:    <EyeIcon sx={{ fontSize: 36 }} />,
    features: [
      "Real-time job and task board across all stations",
      "Touch-screen kiosk view — scan piece marks at the floor",
      "Bill of Lading with load seal chain-of-custody",
      "Shipping load summary and load verification",
      "Process and fabrication workflow management",
      "Native iOS app for field and shipping",
    ],
  },
];

const TRUST_ITEMS = [
  "Built for AWS D1.1 and CWB W47.1 environments",
  "Immutable audit log on every action",
  "Multi-tenant data isolation",
  "Role-based access controls",
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Sign up in 2 minutes",
    body:  "Choose the modules your shop needs, enter your company info, and get instant access. Runs in your browser, nothing to install. Software only: your devices, printers, scanners and network are not included.",
  },
  {
    step: "02",
    title: "Set up your jobs",
    body:  "Import or create jobs, add piece marks, assign fabricated items. Your team is on the floor the same day.",
  },
  {
    step: "03",
    title: "Track everything in real time",
    body:  "Heats logged, CMTRs attached, NCRs raised and closed, loads sealed — all with a full audit trail that survives any QA audit.",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── NAV ──────────────────────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{
          position:  "sticky", top: 0, zIndex: 100,
          bgcolor:   "white",
          borderBottom: "1px solid #e2e8f0",
          px: { xs: 2, md: 6 }, py: 1.5,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <BuildingIcon sx={{ color: "#1565c0", fontSize: 28 }} />
            <Box>
              <Typography fontWeight={800} fontSize={18} color="#1565c0" lineHeight={1}>
                Shop Floor Mgmt (Beta)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Structural Steel Fabrication Platform
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {!isMobile && (
              <Button
                variant="text"
                size="small"
                onClick={() => navigate("/login")}
                sx={{ color: "text.secondary" }}
              >
                Log In
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate("/signup")}
              endIcon={<ArrowIcon />}
            >
              Start Free Trial
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 40%, #1976d2 100%)",
          color:      "white",
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          px: { xs: 3, md: 8 },
          textAlign: "center",
        }}
      >
        <Chip
          label="Built for structural steel fabrication shops"
          size="small"
          sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", mb: 3, fontSize: 12 }}
        />
        <Typography
          variant={isMobile ? "h4" : "h2"}
          fontWeight={800}
          lineHeight={1.15}
          sx={{ maxWidth: 780, mx: "auto", mb: 2 }}
        >
          Traceability, quality, and operations — all in one platform.
        </Typography>
        <Typography
          variant="h6"
          fontWeight={400}
          sx={{ maxWidth: 600, mx: "auto", mb: 5, opacity: 0.88 }}
        >
          SFM gives steel fab shops the tools to track heats, manage NCRs,
          and run live operations with a full audit trail on every action.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/signup")}
            endIcon={<ArrowIcon />}
            sx={{
              bgcolor: "white", color: "#1565c0", fontWeight: 700,
              px: 4, py: 1.5, fontSize: 16,
              "&:hover": { bgcolor: "#e3f2fd" },
            }}
          >
            Start 14-Day Free Trial
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/login")}
            sx={{
              borderColor: "rgba(255,255,255,0.5)", color: "white",
              px: 4, py: 1.5, fontSize: 16,
              "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.08)" },
            }}
          >
            Log In
          </Button>
        </Stack>
        <Typography variant="caption" sx={{ mt: 3, display: "block", opacity: 0.7 }}>
          No credit card required · Cancel anytime · Core platform free · Add-ons $25/mo or $244.99/yr after trial
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, display: "block", opacity: 0.7 }}>
          Software only. Hardware is not included: Apple TV, displays, iPad or iPhone, label printers, scanners and network are supplied by you.
        </Typography>
      </Box>

      {/* ── TRUST BAR ────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: "#1e293b", py: 2, px: { xs: 2, md: 6 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 3 }}
          justifyContent="center"
          alignItems="center"
          flexWrap="wrap"
        >
          {TRUST_ITEMS.map((item) => (
            <Stack key={item} direction="row" spacing={0.75} alignItems="center">
              <CheckIcon sx={{ fontSize: 14, color: "#4ade80" }} />
              <Typography variant="caption" color="#94a3b8">{item}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* ── MODULES ──────────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box textAlign="center" mb={7}>
          <Typography variant="h4" fontWeight={800} mb={1.5}>
            One platform. Add only what your shop needs.
          </Typography>
          <Typography color="text.secondary" variant="h6" fontWeight={400} sx={{ maxWidth: 560, mx: "auto" }}>
            Subscribe to only what your shop needs.
            SFM Operations is included free. SFM Inspection Plus and WeldAssure are $25/month or $244.99/year each, after the free trial.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {MODULES.map((m) => (
            <Grid item xs={12} md={4} key={m.slug}>
              <Card
                sx={{
                  height: "100%",
                  border: `2px solid ${m.color}22`,
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(0,0,0,0.12)" },
                }}
              >
                {/* Card header */}
                <Box sx={{ bgcolor: m.bg, px: 3, pt: 3, pb: 2 }}>
                  <Box sx={{ color: m.color, mb: 1.5 }}>{m.icon}</Box>
                  <Typography variant="h6" fontWeight={800} color={m.color}>{m.label}</Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>{m.tagline}</Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography fontWeight={800} fontSize={22} color={m.color}>{m.price}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.priceNote}</Typography>
                  </Stack>
                </Box>

                {/* Features */}
                <Box sx={{ px: 3, py: 2.5 }}>
                  <Stack spacing={1.25}>
                    {m.features.map((f) => (
                      <Stack key={f} direction="row" spacing={1} alignItems="flex-start">
                        <CheckIcon sx={{ fontSize: 16, color: m.color, mt: 0.3, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">{f}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                {/* CTA */}
                <Box sx={{ px: 3, pb: 3 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate("/signup")}
                    sx={{ bgcolor: m.color, "&:hover": { bgcolor: m.color, filter: "brightness(0.9)" } }}
                  >
                    Start Free Trial
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Core platform note */}
        <Box
          sx={{
            mt: 4, p: 3, bgcolor: "white", borderRadius: 2,
            border: "1px solid #e2e8f0", textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong style={{ color: "#1e293b" }}>Core platform included free</strong> with every account —
            Jobs, Tasks, Sequences, Inventory, Purchase Orders, Contacts, Vendors, Users &amp; Roles.
            SFM Operations is included too. Add paid modules as your shop grows.
          </Typography>
        </Box>
      </Container>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: "#1e293b", py: { xs: 8, md: 12 }, px: { xs: 3, md: 8 } }}>
        <Typography variant="h4" fontWeight={800} color="white" textAlign="center" mb={7}>
          Up and running the same day.
        </Typography>
        <Grid container spacing={4} maxWidth={900} mx="auto">
          {HOW_IT_WORKS.map((h) => (
            <Grid item xs={12} md={4} key={h.step}>
              <Stack spacing={1.5}>
                <Typography
                  fontWeight={900}
                  fontSize={48}
                  color="#1976d2"
                  lineHeight={1}
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {h.step}
                </Typography>
                <Typography variant="h6" fontWeight={700} color="white">{h.title}</Typography>
                <Typography variant="body2" color="#94a3b8" lineHeight={1.7}>{h.body}</Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          px: { xs: 3, md: 8 },
          textAlign: "center",
          background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
        }}
      >
        <Typography variant={isMobile ? "h5" : "h3"} fontWeight={800} mb={2}>
          Ready to bring traceability to your fab shop?
        </Typography>
        <Typography color="text.secondary" variant="h6" fontWeight={400} mb={5} sx={{ maxWidth: 500, mx: "auto" }}>
          Start your 14-day free trial today. No credit card. No setup fees. Cancel anytime.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate("/signup")}
          endIcon={<ArrowIcon />}
          sx={{ px: 5, py: 2, fontSize: 18, fontWeight: 700 }}
        >
          Start Your Free Trial
        </Button>
      </Box>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <Box
        component="footer"
        sx={{
          bgcolor: "#0f172a", color: "#64748b",
          py: 4, px: { xs: 3, md: 8 },
          textAlign: "center",
        }}
      >
        <Typography variant="body2">
          © {new Date().getFullYear()} Shop Floor Mgmt (Beta) · A3 Assurance Inc. ·{" "}
          <Box
            component="span"
            sx={{ cursor: "pointer", "&:hover": { color: "white" } }}
            onClick={() => navigate("/login")}
          >
            Log In
          </Box>
          {" · "}
          <Box
            component="span"
            sx={{ cursor: "pointer", "&:hover": { color: "white" } }}
            onClick={() => navigate("/signup")}
          >
            Sign Up
          </Box>
          {" · "}
          <Box
            component="span"
            sx={{ cursor: "pointer", "&:hover": { color: "white" } }}
            onClick={() => navigate("/terms")}
          >
            Terms of Service
          </Box>
          {" · "}
          <Box
            component="span"
            sx={{ cursor: "pointer", "&:hover": { color: "white" } }}
            onClick={() => navigate("/privacy")}
          >
            Privacy Policy
          </Box>
        </Typography>
      </Box>

    </Box>
  );
}
